import { afterNextRender, Component, inject, Input, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/** Breakpoint, ab dem eingeklappt wird – deckungsgleich mit dem SCSS. */
const MOBILE_MQ = '(max-width: 767.98px)';
import {
  bootstrapCartCheck,
  bootstrapChevronDown,
  bootstrapGearWideConnected,
  bootstrapPersonCheck,
  bootstrapPlayFill,
  bootstrapTruck,
  bootstrapWifi,
} from '@ng-icons/bootstrap-icons';
import { NgIconComponent, provideIcons } from '@ng-icons/core';

export interface ShopFlowStep {
  /** ng-icon-Name, z. B. 'bootstrapCartCheck'. */
  icon: string;
  title: string;
  text: string;
}

/**
 * Ablauf-Banner „von Bestellung bis Lieferung“ – zeigt dem Kunden, wie es nach
 * dem Kauf weitergeht, und markiert die aktuelle Position („Du bist hier“).
 * Wird auf der Shopübersicht und den Produktseiten genutzt. Standard-Schritte
 * gelten für die Haupt-marktbox; Erweiterungen geben eigene Schritte per Input.
 */
@Component({
  selector: 'app-shop-flow',
  standalone: true,
  imports: [NgIconComponent],
  providers: [
    provideIcons({
      bootstrapCartCheck,
      bootstrapPersonCheck,
      bootstrapGearWideConnected,
      bootstrapTruck,
      bootstrapWifi,
      bootstrapPlayFill,
      bootstrapChevronDown,
    }),
  ],
  templateUrl: './shop-flow.component.html',
  styleUrl: './shop-flow.component.scss',
})
export class ShopFlowComponent {
  @Input() eyebrow = $localize`Nach deiner Bestellung`;
  @Input() title = $localize`So kommt deine <span class="tm">marktbox</span> zu dir`;
  @Input() lead = $localize`Vier Schritte von der Bestellung bis zur Lieferung – wir begleiten dich bei jedem.`;
  @Input() outro = $localize`Und dann: bestücken, einloggen, loslegen – dein Verkauf läuft rund um die Uhr.`;
  /** Index des aktuellen Schritts (0-basiert) – bekommt den „Du bist hier“-Marker. */
  @Input() current = 0;
  @Input() steps: ShopFlowStep[] = [
    { icon: 'bootstrapCartCheck', title: $localize`Bestellen`, text: $localize`Du wählst deine marktbox – Kauf oder Miete – und bestellst bequem online.` },
    { icon: 'bootstrapPersonCheck', title: $localize`Anmelden & App-Account`, text: $localize`Du meldest dich für den Bezahldienst an und legst deinen Account in der marktbox-App an.` },
    { icon: 'bootstrapGearWideConnected', title: $localize`Produktion & Test`, text: $localize`Deine marktbox wird in Kiel gefertigt, getestet und startklar aufgesetzt.` },
    { icon: 'bootstrapTruck', title: $localize`Abholung & Lieferung`, text: $localize`Die Spedition holt sie ab und stellt sie sicher verpackt bei dir zu.` },
  ];

  /** YouTube-ID des Produktionsvideos. Gesetzt → Video-Abschnitt unter dem Banner. */
  @Input() productionVideoId?: string;
  @Input() productionTitle = $localize`So entsteht deine marktbox`;
  @Input() productionText =
    $localize`Gefertigt und getestet in Kiel – wirf einen Blick hinter die Kulissen unserer Produktion.`;

  /** Nur mobil einklappbar (z. B. auf den PDPs); Desktop & Shop-Übersicht immer offen. */
  @Input() collapsibleMobile = false;
  /** Aufgeklappt? Gilt nur mobil – Desktop zeigt den Inhalt per CSS immer. */
  readonly open = signal(false);
  /** true nur im mobilen Viewport – nur dann ist der Banner interaktiv/ein Button. */
  readonly isMobile = signal(false);

  constructor() {
    afterNextRender(() => {
      const mql = window.matchMedia(MOBILE_MQ);
      this.isMobile.set(mql.matches);
      mql.addEventListener('change', (e) => this.isMobile.set(e.matches));
    });
  }

  toggle(): void {
    if (this.collapsibleMobile && this.isMobile()) this.open.update((v) => !v);
  }

  private readonly sanitizer = inject(DomSanitizer);
  /** Wird erst nach Klick true – vorher zeigen wir nur ein Vorschaubild (kein YouTube-Cookie/-Tracking). */
  videoOpen = false;

  get posterUrl(): string {
    return `https://i.ytimg.com/vi/${this.productionVideoId}/maxresdefault.jpg`;
  }

  /** Fällt auf das immer vorhandene hqdefault-Bild zurück, falls maxres fehlt. */
  onPosterError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const fallback = `https://i.ytimg.com/vi/${this.productionVideoId}/hqdefault.jpg`;
    if (!img.src.endsWith('hqdefault.jpg')) {
      img.src = fallback;
    }
  }

  get embedUrl(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube-nocookie.com/embed/${this.productionVideoId}?autoplay=1&rel=0&modestbranding=1`,
    );
  }

  openVideo(): void {
    this.videoOpen = true;
  }
}
